import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { projectsApi, usersApi, type HardwareType, type Project, type User } from '../api';
import { useAsync } from '../hooks';
import { Alert, Badge, Button, Card, Checkbox, Empty, Field, Modal, PageHeader, Select, TextArea, errorText } from '../components/ui';

export default function ProjectsPage() {
  const projects = useAsync(projectsApi.list, []);
  const hardware = useAsync(projectsApi.hardwareTypes, []);
  const users = useAsync(usersApi.list, []);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const [notice, setNotice] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);

  const reload = async () => {
    await Promise.all([projects.reload(), hardware.reload()]);
  };
  const act = async (action: () => Promise<unknown>, success?: string) => {
    setNotice(null);
    try {
      await action();
      if (success) setNotice({ kind: 'success', text: success });
      await reload();
    } catch (err) {
      setNotice({ kind: 'error', text: errorText(err) });
    }
  };

  const hwList = hardware.data ?? [];
  const managers = (users.data ?? []).filter((u) => u.role === 'release_manager');

  return (
    <div>
      <PageHeader title="Projects">
        <Button onClick={() => setCreating(true)}>
          <Plus size={16} /> New project
        </Button>
      </PageHeader>

      {notice && (
        <div className="mb-4">
          <Alert kind={notice.kind}>{notice.text}</Alert>
        </div>
      )}
      {projects.error && <Alert kind="error">{projects.error}</Alert>}

      <Card className="mb-4 p-4">
        <h2 className="mb-1 text-sm font-medium">New devices</h2>
        <p className="mb-3 text-xs text-slate-500">
          Devices checking in for the first time are registered into this project's default group. Without a project they wait in "Unassigned devices".
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {hwList.map((hw) => (
            <Select
              key={hw.code}
              label={hw.name}
              value={hw.default_project_id ?? ''}
              onChange={(e) =>
                act(
                  () => projectsApi.setDefaultProject(hw.code, e.target.value ? Number(e.target.value) : null),
                  `Default project for ${hw.name} updated.`,
                )
              }
            >
              <option value="">— none (unassigned) —</option>
              {(projects.data ?? [])
                .filter((p) => p.hardware_type === hw.code)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </Select>
          ))}
        </div>
      </Card>

      {!projects.loading && projects.data?.length === 0 && <Empty>No projects yet.</Empty>}
      <div className="grid gap-3 lg:grid-cols-2">
        {(projects.data ?? []).map((p) => (
          <Card key={p.id} className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h2 className="font-semibold">{p.name}</h2>
                <p className="text-xs text-slate-500">
                  {p.slug} · {hwList.find((h) => h.code === p.hardware_type)?.name ?? p.hardware_type} · {p.device_count} devices · {p.release_count}{' '}
                  releases
                </p>
                {p.description && <p className="mt-1 text-sm text-slate-600">{p.description}</p>}
              </div>
              <Button variant="secondary" onClick={() => setEditing(p)}>
                Edit
              </Button>
            </div>
            <div className="mt-3 text-sm">
              <span className="text-slate-500">Release Managers: </span>
              {p.members.length ? p.members.map((m) => m.name || m.username).join(', ') : <span className="text-slate-400">none</span>}
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {p.groups.map((g) => (
                <Badge key={g.id} tone={g.id === p.default_group_id ? 'blue' : 'slate'} title={g.id === p.default_group_id ? 'Default group' : undefined}>
                  {g.name} ({g.device_count})
                </Badge>
              ))}
            </div>
          </Card>
        ))}
      </div>

      {creating && (
        <CreateProjectModal
          hardware={hwList}
          onClose={() => setCreating(false)}
          onCreated={(p) => {
            setCreating(false);
            setNotice({ kind: 'success', text: `Project ${p.name} created with group "production".` });
            reload();
          }}
        />
      )}
      {editing && (
        <EditProjectModal
          project={editing}
          managers={managers}
          onClose={() => setEditing(null)}
          onChanged={async () => {
            await reload();
          }}
          onDeleted={() => {
            setEditing(null);
            setNotice({ kind: 'success', text: `Project ${editing.name} deleted.` });
            reload();
          }}
        />
      )}
    </div>
  );
}

function CreateProjectModal({ hardware, onClose, onCreated }: { hardware: HardwareType[]; onClose: () => void; onCreated: (p: Project) => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [hw, setHw] = useState(hardware[0]?.code ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    try {
      onCreated(await projectsApi.create({ name, description, hardware_type: hw }));
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <Modal title="New project" onClose={onClose}>
      <div className="space-y-4">
        {error && <Alert kind="error">{error}</Alert>}
        <Field label="Name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Card reader" />
        <Select label="Hardware" value={hw} onChange={(e) => setHw(e.target.value)}>
          {hardware.map((h) => (
            <option key={h.code} value={h.code}>
              {h.name}
            </option>
          ))}
        </Select>
        <TextArea label="Description (optional)" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!name.trim() || !hw}>
            Create
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function EditProjectModal({
  project: initial,
  managers,
  onClose,
  onChanged,
  onDeleted,
}: {
  project: Project;
  managers: User[];
  onClose: () => void;
  onChanged: () => Promise<void>;
  onDeleted: () => void;
}) {
  const [project, setProject] = useState(initial);
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description);
  const [members, setMembers] = useState(new Set(initial.members.map((m) => m.id)));
  const [newGroup, setNewGroup] = useState('');
  const [error, setError] = useState<string | null>(null);

  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      await action();
      setProject(await projectsApi.get(project.id));
      await onChanged();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const toggleMember = (id: number) => {
    const next = new Set(members);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setMembers(next);
  };

  return (
    <Modal title={`Edit ${project.name}`} onClose={onClose} wide>
      <div className="space-y-5">
        {error && <Alert kind="error">{error}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <Field label="Slug (used in download URLs)" value={project.slug} disabled />
        </div>
        <TextArea label="Description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />

        <div>
          <h3 className="mb-2 text-sm font-medium">Release Managers</h3>
          {managers.length === 0 ? (
            <p className="text-sm text-slate-500">No Release Managers exist yet. Create them on the Users page.</p>
          ) : (
            <div className="grid gap-1 sm:grid-cols-3">
              {managers.map((u) => (
                <Checkbox
                  key={u.id}
                  label={
                    <span>
                      {u.name || u.username} <span className="text-xs text-slate-400">{u.username}</span>
                    </span>
                  }
                  checked={members.has(u.id)}
                  onChange={() => toggleMember(u.id)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            onClick={() => run(() => projectsApi.update(project.id, { name, description, member_ids: [...members] }))}
            disabled={!name.trim()}
          >
            Save project
          </Button>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-medium">Groups</h3>
          <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
            {project.groups.map((g) => (
              <li key={g.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <span>
                  {g.name} <span className="text-slate-500">({g.device_count} devices)</span>
                  {g.id === project.default_group_id && (
                    <Badge tone="blue">
                      default
                    </Badge>
                  )}
                </span>
                <span className="flex gap-1">
                  {g.id !== project.default_group_id && (
                    <>
                      <Button variant="secondary" onClick={() => run(() => projectsApi.update(project.id, { default_group_id: g.id }))}>
                        Make default
                      </Button>
                      <Button
                        variant="danger"
                        aria-label={`Delete group ${g.name}`}
                        onClick={() =>
                          confirm(`Delete group ${g.name}? Its devices move to the default group.`) && run(() => projectsApi.removeGroup(g.id))
                        }
                      >
                        <Trash2 size={16} />
                      </Button>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex gap-2">
            <input
              value={newGroup}
              onChange={(e) => setNewGroup(e.target.value)}
              placeholder="New group name, e.g. devel"
              className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            />
            <Button
              variant="secondary"
              disabled={!newGroup.trim()}
              onClick={() =>
                run(async () => {
                  await projectsApi.createGroup(project.id, { name: newGroup.trim() });
                  setNewGroup('');
                })
              }
            >
              Add group
            </Button>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-4">
          <Button
            variant="danger"
            onClick={async () => {
              if (!confirm(`Delete project ${project.name}? This only works when it has no devices and no releases.`)) return;
              try {
                await projectsApi.remove(project.id);
                onDeleted();
              } catch (err) {
                setError(errorText(err));
              }
            }}
          >
            Delete project
          </Button>
        </div>
      </div>
    </Modal>
  );
}
